<template>
  <div>
    <PageTitle :title="pageTitle" />
    <v-container>
      <v-alert v-if="error" type="error" text dense>{{ error }}</v-alert>
      <div v-else-if="!form" class="text-center py-10">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <v-card v-else class="pa-4 pa-md-6">
        <p class="mb-4">
          This is the {{ pageTitle }} page on the public website. The menu name
          is the label visitors see in the website navigation.
        </p>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="form.navLabel"
              label="Menu name"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="form.title"
              label="Page title"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="form.subtitle"
              label="Page subtitle"
              outlined
              dense
              rows="2"
              hide-details
            />
          </v-col>
        </v-row>

        <v-row v-if="section === 'about'">
          <v-col cols="12" md="4">
            <v-text-field v-model="form.eyebrow" label="Header label" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.storyEyebrow" label="Story label" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.storyTitle" label="Story heading" outlined dense hide-details />
          </v-col>
          <v-col cols="12">
            <v-textarea v-model="form.storySubtitle" label="Story introduction" outlined dense rows="2" hide-details />
          </v-col>
          <v-col cols="12">
            <v-textarea v-model="form.paragraph1" label="First paragraph" outlined dense rows="3" hide-details />
          </v-col>
          <v-col cols="12">
            <v-textarea v-model="form.paragraph2" label="Second paragraph" outlined dense rows="3" hide-details />
          </v-col>
        </v-row>

        <v-row v-if="section === 'contact'">
          <v-col cols="12" md="4">
            <v-text-field v-model="form.eyebrow" label="Header label" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="8">
            <v-text-field v-model="form.address" label="Address" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.phoneDisplay" label="Phone" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.phone2Display" label="Second phone" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.email" label="Email" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.plusCode" label="Plus code" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.hours" label="Reception hours" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.mapsUrl" label="Google Maps link" outlined dense hide-details />
          </v-col>
        </v-row>

        <v-row v-if="section === 'rooms'">
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="form.taxPercent"
              label="Tax (%)"
              type="number"
              min="0"
              max="100"
              outlined
              dense
              persistent-hint
              hint="Added on the booking summary. Use 0 to hide the tax line."
            />
          </v-col>
          <v-col cols="12" md="8" class="d-flex align-center">
            <v-switch
              v-model="form.taxIncluded"
              inset
              hide-details
              class="mt-0"
              label="Room price already includes this tax"
            />
          </v-col>
        </v-row>

        <v-row v-if="section === 'restaurant'">
          <v-col cols="12">
            <h2 class="text-h6 mb-0">Takeaway charges</h2>
            <p class="mb-0 text--secondary">
              These apply on the website cart. Turn a charge off to hide it. The earliest pickup time is counted from the moment the guest orders.
            </p>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-switch
              v-model="form.packingEnabled"
              inset
              hide-details
              class="mt-0"
              label="Charge packing"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="form.packingAmount"
              label="Packing (LKR per dish)"
              type="number"
              min="0"
              outlined
              dense
              hide-details
              :disabled="!form.packingEnabled"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="form.pickupLeadMinutes"
              label="Earliest pickup (minutes)"
              type="number"
              min="0"
              outlined
              dense
              persistent-hint
              hint="Guests cannot pick a time sooner than this. 25 means 25 minutes from now."
            />
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-switch
              v-model="form.taxEnabled"
              inset
              hide-details
              class="mt-0"
              label="Charge tax"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="form.takeawayTaxPercent"
              label="Takeaway tax (%)"
              type="number"
              min="0"
              max="100"
              outlined
              dense
              hide-details
              :disabled="!form.taxEnabled"
            />
          </v-col>
        </v-row>

        <div v-if="hasItems" class="mt-6">
          <div class="d-flex align-center mb-3">
            <h2 class="text-h6 mb-0">{{ itemHeading }}</h2>
            <v-spacer />
            <v-btn v-if="section !== 'restaurant'" small depressed color="primary" @click="addItem">
              <v-icon left small>mdi-plus</v-icon>
              Add
            </v-btn>
          </div>
          <p v-if="section === 'restaurant'" class="mb-3">
            Dishes come from KOT. Create the dish under Dishes, including a category and a type price.
            Here you set the website name, description, photo, and vegetarian or spicy labels.
          </p>
          <v-expansion-panels>
            <v-expansion-panel v-for="(item, index) in form.items" :key="item.id || index">
              <v-expansion-panel-header>
                {{ item.name || 'New item' }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <template v-if="section === 'rooms'">
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="item.roomId"
                        :items="roomOptions"
                        item-text="name"
                        item-value="id"
                        label="Room"
                        outlined
                        dense
                        hide-details
                        @change="selectRoom(item)"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="item.name"
                        label="Display room name"
                        outlined
                        dense
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="item.roomTypeId"
                        :items="typesFor(item)"
                        item-text="name"
                        item-value="id"
                        label="Room type"
                        outlined
                        dense
                        hide-details
                        :disabled="!item.roomId"
                        @change="applyRate(item)"
                      />
                    </v-col>
                  </template>
                  <template v-else-if="section === 'restaurant'">
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="item.name"
                        label="Display dish name"
                        outlined
                        dense
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        :value="item.category"
                        label="Category"
                        outlined
                        dense
                        readonly
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        :value="item.price"
                        label="Price (LKR)"
                        type="number"
                        outlined
                        dense
                        readonly
                        persistent-hint
                        hint="Default type price from the KOT dish."
                      />
                    </v-col>
                  </template>
                  <template v-else>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="item.name" label="Name" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field v-model="item.price" label="Price (LKR)" type="number" outlined dense hide-details />
                    </v-col>
                  </template>
                  <v-col cols="12">
                    <div class="d-flex flex-column flex-sm-row">
                      <v-img
                        v-if="item.image"
                        :src="imageSrc(item.image)"
                        max-width="160"
                        height="100"
                        class="rounded mr-sm-4 mb-3"
                        contain
                      />
                      <div class="flex-grow-1">
                        <v-file-input
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          label="Upload image"
                          outlined
                          dense
                          hide-details
                          prepend-icon=""
                          prepend-inner-icon="mdi-camera"
                          :loading="item.uploading"
                          @change="uploadImage(item, $event)"
                        />
                        <v-text-field
                          class="mt-3"
                          v-model="item.image"
                          label="Or paste an image URL"
                          outlined
                          dense
                          hide-details
                          placeholder="https://... or /images/hotel/photo-1.jpg"
                          @change="useImageUrl(item)"
                        />
                      </div>
                    </div>
                  </v-col>

                  <template v-if="section === 'rooms'">
                    <v-col cols="12" md="3">
                      <v-text-field
                        :value="item.price"
                        label="Price (LKR)"
                        type="number"
                        outlined
                        dense
                        readonly
                        persistent-hint
                        :hint="priceHint(item)"
                      />
                    </v-col>
                    <v-col cols="6" md="3">
                      <v-text-field v-model="item.capacity" label="Guests" type="number" outlined dense hide-details />
                    </v-col>
                    <v-col cols="6" md="3">
                      <v-text-field v-model="item.beds" label="Beds" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="item.size" label="Size" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12">
                      <v-textarea v-model="item.description" label="Description" outlined dense rows="2" hide-details />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="item.amenitiesText"
                        label="Amenities, separated by commas"
                        outlined
                        dense
                        hide-details
                      />
                    </v-col>
                  </template>

                  <template v-if="section === 'hall'">
                    <v-col cols="12" md="4">
                      <v-text-field v-model="item.capacity" label="Capacity" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field v-model="item.area" label="Area" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field v-model="item.bestFor" label="Best for" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="item.featuresText"
                        label="Features, separated by commas"
                        outlined
                        dense
                        hide-details
                      />
                    </v-col>
                  </template>

                  <template v-if="section === 'restaurant'">
                    <v-col cols="12" md="4">
                      <v-switch
                        v-model="item.onWebsite"
                        label="Show on website"
                        inset
                        dense
                        hide-details
                        class="mt-0"
                      />
                    </v-col>
                    <v-col cols="6" md="2">
                      <v-switch v-model="item.veg" label="Vegetarian" inset dense hide-details class="mt-0" />
                    </v-col>
                    <v-col cols="6" md="2">
                      <v-switch v-model="item.spicy" label="Spicy" inset dense hide-details class="mt-0" />
                    </v-col>
                    <v-col cols="12">
                      <v-textarea v-model="item.description" label="Description" outlined dense rows="2" hide-details />
                    </v-col>
                  </template>

                  <v-col v-if="section !== 'restaurant'" cols="12" class="text-right">
                    <v-btn text color="error" @click="removeItem(index)">Remove</v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>

        <div class="mt-6">
          <v-btn color="primary" depressed :loading="saving" @click="save">
            Save website page
          </v-btn>
        </div>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import http from "@/plugins/axios";
import env from "@/config/env";

const TITLES = {
  rooms: "Website Rooms",
  hall: "Website Banquet Hall",
  restaurant: "Website Restaurant",
  about: "Website About",
  contact: "Website Contact",
};

export default {
  data() {
    return {
      form: null,
      error: "",
      saving: false,
      roomOptions: [],
    };
  },
  computed: {
    section() {
      return this.$route.meta.section;
    },
    pageTitle() {
      return TITLES[this.section] || "Website";
    },
    hasItems() {
      return ["rooms", "hall", "restaurant"].indexOf(this.section) !== -1;
    },
    itemHeading() {
      if (this.section === "rooms") return "Rooms";
      if (this.section === "hall") return "Halls";
      return "Dishes";
    },
  },
  watch: {
    section: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    load() {
      this.form = null;
      this.error = "";
      http
        .get("hms/site-pages/" + this.section)
        .then((res) => {
          this.form = this.prepare(res.data);
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          this.error = (data && data.message) || "Could not load this page.";
        });
    },
    prepare(data) {
      const form = JSON.parse(JSON.stringify(data || {}));
      this.roomOptions = Array.isArray(form.roomOptions) ? form.roomOptions : [];
      delete form.roomOptions;
      delete form.rateOptions;
      if (this.section === "rooms") {
        form.taxPercent =
          form.taxPercent == null || form.taxPercent === ""
            ? 12
            : Number(form.taxPercent);
        form.taxIncluded = Boolean(form.taxIncluded);
      }
      if (this.section === "restaurant") {
        form.packingEnabled = form.packingEnabled !== false;
        form.packingAmount =
          form.packingAmount == null || form.packingAmount === ""
            ? 10
            : Number(form.packingAmount);
        form.taxEnabled = form.taxEnabled !== false;
        form.takeawayTaxPercent =
          form.takeawayTaxPercent == null || form.takeawayTaxPercent === ""
            ? 5
            : Number(form.takeawayTaxPercent);
        form.pickupLeadMinutes =
          form.pickupLeadMinutes == null || form.pickupLeadMinutes === ""
            ? 25
            : Number(form.pickupLeadMinutes);
      }
      form.items = (form.items || []).map((item) => ({
        ...item,
        onWebsite: item.onWebsite !== false,
        roomId: item.roomId ? Number(item.roomId) : null,
        roomTypeId: item.roomTypeId ? Number(item.roomTypeId) : null,
        amenitiesText: Array.isArray(item.amenities)
          ? item.amenities.join(", ")
          : item.amenities || "",
        featuresText: Array.isArray(item.features)
          ? item.features.join(", ")
          : item.features || "",
      }));
      return form;
    },
    typesFor(item) {
      const room = this.roomOptions.find((row) => row.id === item.roomId);
      return room && room.types ? room.types : [];
    },
    selectRoom(item) {
      const room = this.roomOptions.find((row) => row.id === item.roomId);
      if (!room) {
        item.roomTypeId = null;
        item.price = 0;
        item.type = "";
        return;
      }
      item.name = room.name;
      const types = room.types || [];
      const current = types.find((type) => type.id === item.roomTypeId);
      if (!current) item.roomTypeId = types.length === 1 ? types[0].id : null;
      this.applyRate(item);
    },
    applyRate(item) {
      const type = this.typesFor(item).find((row) => row.id === item.roomTypeId);
      item.price = type ? type.price : 0;
      if (type) item.type = type.name;
    },
    priceHint(item) {
      if (!item.roomId) return "Choose a room from the Rooms tab.";
      if (!item.roomTypeId) return "Choose a room type for this room.";
      if (!item.price) return "Set the amount for this room and type on Rooms.";
      return "Amount set on Rooms for this room and type.";
    },
    addItem() {
      const id = this.section + "-" + Date.now();
      if (this.section === "rooms") {
        this.form.items.push({
          id,
          name: "",
          roomId: null,
          roomTypeId: null,
          type: "",
          price: 0,
          capacity: 2,
          beds: "",
          size: "",
          description: "",
          image: "/images/hotel/photo-2.jpg",
          amenitiesText: "",
        });
      } else if (this.section === "hall") {
        this.form.items.push({
          id,
          name: "",
          capacity: "",
          area: "",
          bestFor: "",
          price: 0,
          image: "/images/hotel/photo-1.jpg",
          featuresText: "",
        });
      } else {
        this.form.items.push({
          id,
          name: "",
          category: "Mains",
          price: 0,
          veg: false,
          spicy: false,
          description: "",
          image: "",
        });
      }
    },
    removeItem(index) {
      this.form.items.splice(index, 1);
    },
    imageSrc(src) {
      if (!src) return "";
      if (/^https?:\/\//.test(src)) return src;
      if (src.startsWith("/images/")) {
        const host = window.location.hostname;
        const site =
          host === "localhost" || host === "127.0.0.1"
            ? "http://localhost:5173"
            : "https://gnanams.com";
        return site + src;
      }
      return src;
    },
    useImageUrl(item) {
      const url = (item.image || "").trim();
      item.image = url;
      item.gallery = url ? [url] : [];
    },
    uploadImage(item, file) {
      const picked = Array.isArray(file) ? file[0] : file;
      if (!picked) return;
      this.$set(item, "uploading", true);
      const body = new FormData();
      body.append("image", picked);
      const token = localStorage.getItem("token");
      fetch(env.apiUrl + "hms/site-pages/upload", {
        method: "POST",
        headers: token ? { Authorization: "Bearer " + token } : {},
        body,
      })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data.message || "Could not upload the image.");
          }
          item.image = data.url;
          item.gallery = [data.url];
          this.$toast.success("Image uploaded. Save the page to publish it.");
        })
        .catch((err) => {
          this.$toast.error(err.message || "Could not upload the image.");
        })
        .finally(() => {
          this.$set(item, "uploading", false);
        });
    },
    save() {
      this.saving = true;
      http
        .put("hms/site-pages/" + this.section, this.form)
        .then((res) => {
          this.form = this.prepare(res.data);
          this.$toast.success("Website page saved");
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          this.$toast.error((data && data.message) || "Could not save this page.");
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>
